import useRegisteredModuleFilters from '../../moduleFilters';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSession } from '@/app/providers/session-provider';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { ConfirmActionDialog, PageActionRow, PageDivider, PageFrame, PrimaryActionButton } from '../../../shared/components/patterns';
import { toast } from '../../../shared/hooks/use-toast';
import {
  CreateDocumentTypeDialog,
  DocumentDetailDialog,
  DocumentsFilters,
  DocumentsHeader,
  DocumentsStats,
  DocumentsTable,
  EditDocumentMetadataDialog,
  UploadDocumentDialog,
} from '@/modules/documents/components';
import {
  useContractForm,
  useCreateDocumentFlow,
  useDocumentsLibraryData,
  useMandateForm,
  useUploadDocument,
} from '@/modules/documents/hooks';
import {
  downloadDocumentFileAuthenticated,
  openDocumentPreviewInNewTab,
} from '@/modules/documents/services';
import { buildDocumentMetrics } from '../components/library/documentsPage.config';
import DocumentsMetricDialog from '../components/library/DocumentsMetricDialog';
import { filterDocumentsByOverview } from '../utils/documents.overview';
import './documents-page.css';

export default function DocumentsPage() {
  const { capabilities, currentUser } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId') || '';
  const library = useDocumentsLibraryData({ initialClientId: clientIdParam });
  const moduleFilters = useRegisteredModuleFilters('documents', library.documents);
  const [metricDialog, setMetricDialog] = useState(null);
  const [focusComments, setFocusComments] = useState(false);
  const [detailDocument, setDetailDocument] = useState(null);
  const [previewingDocumentId, setPreviewingDocumentId] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [editingDocument, setEditingDocument] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const upload = useUploadDocument({
    clients: library.clients,
    staff: library.staff,
    defaultManagedBy: currentUser?.name || currentUser?.email || '',
    onUpload: library.registerDocument,
  });
  const createFlow = useCreateDocumentFlow();
  const mandateForm = useMandateForm({
    clients: library.clients,
    onCreate: library.registerDocument,
  });
  const contractForm = useContractForm({
    clients: library.clients,
    onCreate: library.registerDocument,
  });

  async function handleMandateSubmit() {
    const created = await mandateForm.submit();
    if (created) createFlow.closeDialog();
  }

  async function handleContractSubmit() {
    const created = await contractForm.submit();
    if (created) createFlow.closeDialog();
  }

  async function openPreview(document) {
    if (!document || previewingDocumentId) {
      return;
    }

    setPreviewError('');
    setPreviewingDocumentId(String(document.id || document.previewUrl || document.fileName || 'preview'));

    try {
      const result = await openDocumentPreviewInNewTab(document);
      if (result?.ok === false) {
        setPreviewError(result.message || 'Anteprima documento non disponibile.');
        return;
      }
    } finally {
      setPreviewingDocumentId('');
    }
  }

  async function handleDownloadDocument(document) {
    const result = await downloadDocumentFileAuthenticated(document);
    if (result?.ok === false) {
      toast({ title: 'Download non disponibile', description: result.message, variant: 'destructive' });
    }
  }

  async function handleUpdateDocumentMetadata(metadata) {
    if (!editingDocument) {
      return { ok: false, message: 'Documento non selezionato.' };
    }

    return library.updateDocumentMetadata(editingDocument.id, metadata);
  }

  async function handleDeleteDocument(document) {
    if (!capabilities?.canManageDocumentDestructiveActions) {
      return;
    }

    setDeleteRequest(document);
  }

  const selectedClient = library.clients.find(
    (client) => String(client.id) === String(library.selectedClientId)
  );
  const documentsModuleDisabled = library.documentsCatalogStatus === 'module_disabled';
  const documentsTenantDatabaseUnavailable = library.documentsCatalogStatus === 'tenant_database_unavailable';
  const documentMetrics = buildDocumentMetrics(library.clientScopedDocuments || library.documents);

  function clearClientContext() {
    library.setSelectedClientId('tutti');
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('clientId');
    setSearchParams(nextParams, { replace: true });
  }

  return (
    <PageFrame filterModel={moduleFilters} className="documents-page">
      {!documentsModuleDisabled && !documentsTenantDatabaseUnavailable ? (
        <DocumentsHeader onCreate={createFlow.openDialog} onUpload={upload.openDialog} />
      ) : null}

      {documentsModuleDisabled || documentsTenantDatabaseUnavailable ? (
        <Alert className="documents-page__alert">
          <AlertDescription>
            {library.documentsError || (
              documentsModuleDisabled
                ? 'Modulo documenti non abilitato per questo workspace.'
                : 'Database tenant non disponibile per il modulo documenti.'
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {!documentsModuleDisabled && !documentsTenantDatabaseUnavailable ? (
        <DocumentsStats
          stats={documentMetrics}
          activeMetric={library.activeOverviewFilter}
          onMetricClick={(key) => {
            moduleFilters.onReset();
            library.setOverviewFilter(key, { toggle: false });
            setMetricDialog(['overdue', 'toComplete'].includes(key) ? key : null);
          }}
        />
      ) : null}

      {!documentsModuleDisabled && !documentsTenantDatabaseUnavailable && library.selectedClientId !== 'tutti' ? (
        <div className="documents-page__client-context">
          <span>
            Vista filtrata per cliente: {selectedClient?.name || `Cliente ${library.selectedClientId}`}.
          </span>
          <button type="button" onClick={clearClientContext}>
            Mostra tutti
          </button>
        </div>
      ) : null}

      {!documentsModuleDisabled && !documentsTenantDatabaseUnavailable ? (
        <>
        <PageDivider />
        <DocumentsFilters
          categories={library.categories}
          searchTerm={library.searchTerm}
          onSearchChange={library.setSearchTerm}
          selectedCategory={library.selectedCategory}
          onCategoryChange={library.setSelectedCategory}
          selectedStatuses={library.selectedStatuses}
          onStatusesChange={library.setSelectedStatuses}
          activeOverviewFilter={library.activeOverviewFilter}
          onOverviewChange={(key) => library.setOverviewFilter(key, { toggle: false })}
          onReset={library.resetFilters}
        />
        <PageDivider />
        <PageActionRow>
          <PrimaryActionButton iconKey="actions.add" onClick={createFlow.openDialog}>Nuovo</PrimaryActionButton>
          <PrimaryActionButton iconKey="actions.upload" onClick={upload.openDialog}>Carica</PrimaryActionButton>
        </PageActionRow>
        </>
      ) : null}

      {!documentsModuleDisabled ? (
        <>
        {previewError ? (
          <Alert className="documents-page__alert">
            <AlertDescription>{previewError}</AlertDescription>
          </Alert>
        ) : null}
        <DocumentsTable
          documents={moduleFilters.filterRows(library.filteredDocuments)}
          onPreview={openPreview}
          onViewNotes={(document) => { setFocusComments(true); setDetailDocument(document); }}
          onDownload={handleDownloadDocument}
          onEdit={setEditingDocument}
          onDelete={handleDeleteDocument}
          onRowClick={(document) => { setFocusComments(false); setDetailDocument(document); }}
          canManageDocuments={capabilities?.canManageDocumentDestructiveActions === true}
          isLoading={library.isLoadingDocuments}
          documentsError={library.documentsError}
          usingCachedSnapshot={library.usingCachedSnapshot}
          documentsSnapshotStale={library.documentsSnapshotStale}
          previewingDocumentId={previewingDocumentId}
        />
        </>
      ) : null}

      <DocumentsMetricDialog
        metric={metricDialog}
        documents={filterDocumentsByOverview(library.clientScopedDocuments || library.documents, metricDialog)}
        onClose={() => setMetricDialog(null)}
        onSelect={(document) => {
          setMetricDialog(null);
          setFocusComments(false);
          setDetailDocument(document);
        }}
      />

      {!documentsModuleDisabled && !documentsTenantDatabaseUnavailable ? (
        <DocumentDetailDialog
          document={detailDocument}
          clients={library.clients}
          staff={library.staff}
          focusComments={focusComments}
          isOpen={Boolean(detailDocument)}
          onClose={() => { setDetailDocument(null); setFocusComments(false); }}
          onEdit={(document) => {
            setDetailDocument(null);
            setEditingDocument(document);
          }}
          onSave={async (draft) => {
            const result = await library.updateDocumentMetadata(draft.id, draft);
            if (result?.ok) setDetailDocument(result.data.document);
            return result;
          }}
          onDownload={handleDownloadDocument}
          onDelete={(document) => {
            setDetailDocument(null);
            handleDeleteDocument(document);
          }}
          canManageDocuments={capabilities?.canManageDocumentDestructiveActions === true}
        />
      ) : null}

      {!documentsModuleDisabled && !documentsTenantDatabaseUnavailable ? (
        <EditDocumentMetadataDialog
          open={Boolean(editingDocument)}
          document={editingDocument}
          clients={library.clients}
          staff={library.staff}
          categories={library.categories}
          onOpenChange={(open) => {
            if (!open) setEditingDocument(null);
          }}
          onSubmit={handleUpdateDocumentMetadata}
        />
      ) : null}

      {!documentsModuleDisabled && !documentsTenantDatabaseUnavailable ? (
        <UploadDocumentDialog
          open={upload.isUploadDialogOpen}
          onOpenChange={(open) => (open ? upload.openDialog() : upload.closeDialog())}
          clients={library.clients}
          staff={library.staff}
          clientsUnavailable={library.clientsUnavailable}
          staffUnavailable={library.staffUnavailable}
          dragActive={upload.dragActive}
          isUploading={upload.isUploading}
          uploadedFile={upload.uploadedFile}
          error={upload.error}
          form={upload.form}
          onFieldChange={upload.updateField}
          onFileInput={upload.handleFileInput}
          onDrag={upload.handleDrag}
          onDrop={upload.handleDrop}
          onRemoveFile={() => upload.setUploadedFile(null)}
          onSubmit={upload.submit}
          onCancel={upload.closeDialog}
        />
      ) : null}

      {!documentsModuleDisabled && !documentsTenantDatabaseUnavailable ? (
        <CreateDocumentTypeDialog
          open={createFlow.isOpen}
          onOpenChange={(open) => (open ? createFlow.openDialog() : createFlow.closeDialog())}
          selectedDocType={createFlow.selectedDocType}
          onSelectDocType={createFlow.setSelectedDocType}
          onBack={() => createFlow.setSelectedDocType(null)}
          clients={library.clients}
          clientsUnavailable={library.clientsUnavailable}
          mandateForm={{
            form: mandateForm.form,
            isSubmitting: mandateForm.isSubmitting,
            onFieldChange: mandateForm.updateField,
            onSubmit: handleMandateSubmit,
          }}
          contractForm={{
            form: contractForm.form,
            totals: contractForm.totals,
            isSubmitting: contractForm.isSubmitting,
            onFieldChange: contractForm.updateField,
            onCreditRowChange: contractForm.updateCreditRow,
            onAddCreditRow: contractForm.addCreditRow,
            onRemoveCreditRow: contractForm.removeCreditRow,
            onTrancheChange: contractForm.updateTranche,
            onAddTranche: contractForm.addTranche,
            onRemoveTranche: contractForm.removeTranche,
            onSubmit: handleContractSubmit,
          }}
        />
      ) : null}

      <ConfirmActionDialog
        open={Boolean(deleteRequest)}
        title="Eliminare il documento?"
        description={`Il documento "${deleteRequest?.name || 'senza nome'}" verrà eliminato definitivamente.`}
        confirmLabel="Elimina"
        cancelLabel="Annulla"
        tone="danger"
        onCancel={() => setDeleteRequest(null)}
        onConfirm={async () => {
          const document = deleteRequest;
          const result = await library.deleteDocument(document.id);
          setDeleteRequest(null);
          toast({
            title: result?.ok === false ? 'Eliminazione non riuscita' : 'Documento eliminato',
            description: result?.message,
            variant: result?.ok === false ? 'destructive' : 'default',
          });
        }}
      />
    </PageFrame>
  );
}
